import { Injectable } from '@angular/core';
import { Targeting } from 'src/app/classes/targeting/targeting';
import { environment } from 'src/environments/environment';
import { SessionProviderService } from '../sp-authentication/session-provider.service';

export interface AiMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  audienceSize?: number;
  audience?: Targeting;
  isError?: boolean;
  retryPrompt?: string;
}

export interface AudienceGenerationResult {
  targeting: Targeting | null;
  audienceSize: number;
  score_v2: string;
  audience_name: string;
  userResponse: string;
}

export interface BuilderHistory {
  role: 'user' | 'assistant';
  content: string;
}

export interface BuilderCallbacks {
  onStatus?: (message: string) => void;
}

interface BuilderProgressEvent {
  type: 'progress';
  data: string;
}

interface BuilderToolEvent {
  type: 'tool-input-start' | 'tool-input-end';
  toolName: string;
}

interface BuilderResultPayload {
  targeting_spec?: any;
  audience_size?: number;
  score_v2?: string;
  audience_name?: string;
  user_response: string;
}

interface BuilderResultEvent {
  type: 'result';
  data: string; // JSON-stringified BuilderResultPayload
}

interface BuilderErrorEvent {
  type: 'error';
  message: string;
}

type BuilderEvent = BuilderProgressEvent | BuilderToolEvent | BuilderResultEvent | BuilderErrorEvent;

const MAX_HISTORY_TURNS = 20;

@Injectable({
  providedIn: 'root'
})
export class AiAudienceGenerationService {
  private readonly BASE_URL = environment.config.api.diBaseUrl;

  constructor(private sessionProvider: SessionProviderService) {}

  async buildAudience(
    description: string,
    history: BuilderHistory[],
    callbacks: BuilderCallbacks = {},
    conversationId?: string
  ): Promise<AudienceGenerationResult> {
    const token = await this.getToken();

    const cappedHistory = history.length > MAX_HISTORY_TURNS
      ? history.slice(history.length - MAX_HISTORY_TURNS)
      : history;

    const response = await fetch(`${this.BASE_URL}meta/audiences/targeting-spec/builder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
        'Accept-Encoding': 'identity',
      },
      body: JSON.stringify({ description, history: cappedHistory, ...(conversationId ? { conversationId } : {}) }),
    });

    if (!response.ok) {
      const body = await response.text();
      let message = 'Failed to build audience';
      try { message = JSON.parse(body)?.message ?? message; } catch {}
      throw new Error(message);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    return new Promise<AudienceGenerationResult>((resolve, reject) => {
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let settled = false;

      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        reader.cancel().catch(() => {});
        fn();
      };

      const processChunk = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            let event: BuilderEvent | undefined;
            try {
              event = JSON.parse(line.slice(6)) as BuilderEvent;
            } catch {
              continue;
            }
            if (!event) continue;

            if (event.type === 'progress') {
              callbacks.onStatus?.(event.data);
            } else if (event.type === 'tool-input-start') {
              callbacks.onStatus?.(event.toolName);
            } else if (event.type === 'result') {
              const payload: BuilderResultPayload = JSON.parse(event.data);
              let targeting: Targeting | null = null;
              if (payload.targeting_spec) {
                targeting = Targeting.fromJson(JSON.stringify(payload.targeting_spec), 'sociodemo');
              }
              settle(() => resolve({
                targeting,
                audienceSize: payload.audience_size ?? 0,
                score_v2: payload.score_v2 ?? '',
                audience_name: payload.audience_name ?? '',
                userResponse: payload.user_response ?? '',
              }));
              return;
            } else if (event.type === 'error') {
              const errEvent = event as BuilderErrorEvent;
              settle(() => reject(new Error(errEvent.message ?? 'Stream error')));
              return;
            }
          }
        }

        settle(() => reject(new Error('Stream ended without a result')));
      };

      processChunk().catch((err) => settle(() => reject(err)));
    });
  }

  private async getToken(): Promise<string> {
    if (!this.sessionProvider.activeSession) {
      throw new Error('No active session');
    }
    return this.sessionProvider.activeSession.authTokenProvider.getAccessToken();
  }
}
